/**
 * Model CRUD mutation hooks for the AC-1377 React Query migration
 * (AC-1408 / Phase 4.2).
 *
 * Implements the contract from the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`:
 *   - Section 2.6 - mutation + invalidation contract (model rows)
 *   - Section 4   - Phase 4 project resources
 *   - Section 5.3 - mutation hook signatures
 *
 * ## Why this exists
 *
 * Model CRUD currently happens via `useProjectStore.{createModel,
 * updateModel,deleteModel}`, which call `apiService.*` directly and then
 * mutate the project-store `models` and `allModels` arrays in-place. Every
 * consumer (`AgentsPage`, `AgentFormModal`, `ToolManagementSystem`,
 * `InlineModelCreator`, `RegistriesPage`) reads from the same Zustand slice;
 * cross-component refresh works only because the slice is module-level
 * singleton state.
 *
 * That pattern is exactly the shape that produced AC-1390 / AC-1391 /
 * AC-1394 (stale-modal-data class) for workflows. These hooks centralize
 * the contract: every model mutation knows it must invalidate
 * `[project, projectId, models]` AND `[project, projectId, registries]`,
 * and call sites get atomic "edit model -> list, modals, AND registries
 * page all refresh" semantics. The project-store consumers are NOT
 * migrated by this ticket - that's AC-1411. The store slices remain wired
 * up during the transition; AC-1412 removes them once nothing reads from
 * them. This ticket is intentionally pessimistic per TD section 2.7.
 *
 * ## Why projectId at hook level (closure), not mutate-time
 *
 * Model CRUD is always scoped to the SAME project for the lifetime of the
 * surface that triggers it: `AgentsPage` opens an inline-model creator for
 * the current project, `AgentFormModal` updates models in the current
 * project, `RegistriesPage` lives inside a project's route. Closing
 * `projectId` at hook construction time gives us:
 *
 *   1. Cleaner call sites: `mutate({ data })` /
 *      `mutate({ modelId, data })` / `mutate({ modelId })` instead of
 *      threading `projectId` through every callsite.
 *   2. A single source of truth for which project's cache keys to invalidate
 *      - the mutation cannot accidentally invalidate the wrong project.
 *   3. Symmetry with `useModels(projectId)` and the agent / step mutation
 *      patterns.
 *
 * The TD's section-5.3 signatures
 * (`useUpdateModel(projectId, modelId)` / `useDeleteModel(projectId, modelId)`)
 * are summarized form; in practice modelId varies per `mutate()` call within
 * the same surface (e.g. clicking "Delete" on different rows), so the
 * implementation takes only `projectId` at hook level. This matches the
 * orchestrator's instruction for AC-1408.
 *
 * ## Why we invalidate registries on every model mutation
 *
 * Registry-enabled state (`Model.registry_enabled`,
 * `Model.registry_max_entries`, `Model.registry_dedup_fields`) is derived
 * from columns on the same model row. The
 * `queryKeys.projectRegistries(projectId)` cache slot returns
 * `RegistryEnabledModel[]` (the subset of models with `registry_enabled =
 * true`), so:
 *
 *   - Creating a model with `registry_enabled: true` should make it appear
 *     in the registries list.
 *   - Updating a model to flip `registry_enabled` from false to true (or
 *     vice versa) should add or remove it from the registries list.
 *   - Deleting a registry-enabled model should drop it from the registries
 *     list.
 *
 * The cheapest way to keep the two slots consistent is to invalidate both
 * on every model mutation - we cannot infer from the call site alone
 * whether the registry-enabled flag changed (the form's `data` payload may
 * not even include the field). TD section 2.6 mandates this:
 * `useCreateModel`, `useUpdateModel`, `useDeleteModel` ALL invalidate both
 * `projectModels` and `projectRegistries`.
 *
 * ## API
 *
 *   const create = useCreateModel(projectId);
 *   await create.mutateAsync({ data: { class_name: 'X', description: 'Y', properties: {} } });
 *
 *   const update = useUpdateModel(projectId);
 *   await update.mutateAsync({ modelId: 42, data: { description: 'Renamed' } });
 *
 *   const remove = useDeleteModel(projectId);
 *   await remove.mutateAsync({ modelId: 42 });
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import { CreateModelForm, Model } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Create-model input. `data` is the form payload; `apiService.createModel`
 * threads `project_id` server-side from the projectId captured in the hook
 * closure, so callers do not need to repeat it.
 */
export type CreateModelVariables = {
  data: CreateModelForm;
};

/**
 * Update-model input. `modelId` is the canonical numeric id from the server
 * (`Model.model_id`); we accept `string | number` to match how callers
 * receive it (route params are strings, store ids are numbers). `data` is a
 * partial because in practice model modals send only the fields they touched.
 */
export type UpdateModelVariables = {
  modelId: string | number;
  data: Partial<CreateModelForm>;
};

/**
 * Delete-model input. `modelId` only - the projectId is in the closure.
 */
export type DeleteModelVariables = {
  modelId: string | number;
};

/**
 * Helper: invalidate the two model-related keys for a given projectId.
 *
 * Extracted to a private helper so all three mutations stay in sync if the
 * invalidation contract grows (e.g. if a future ticket adds a derived
 * `projectModelStats` key that also depends on model state).
 */
function invalidateModelKeys(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string | number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.projectModels(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.projectRegistries(projectId),
  });
}

/**
 * `useCreateModel(projectId)` - mutation hook for
 * `POST /api/v1/projects/:projectId/models`.
 *
 * @param projectId The project that will own the new model. Captured in
 *                  closure so the mutation always invalidates the right
 *                  project's cache keys.
 *
 * @example
 *   const create = useCreateModel(projectId);
 *   await create.mutateAsync({ data: form });
 */
export function useCreateModel(
  projectId: string | number,
): UseMutationResult<Model, Error, CreateModelVariables> {
  const queryClient = useQueryClient();
  return useMutation<Model, Error, CreateModelVariables>({
    mutationFn: ({ data }) =>
      apiService.createModel(String(projectId), data),
    onSuccess: () => {
      invalidateModelKeys(queryClient, projectId);
    },
  });
}

/**
 * `useUpdateModel(projectId)` - mutation hook for
 * `PUT /api/v1/projects/:projectId/models/:modelId`.
 *
 * @param projectId The project that owns the model being updated.
 *
 * @example
 *   const update = useUpdateModel(projectId);
 *   await update.mutateAsync({ modelId: 42, data: { description: 'Renamed' } });
 */
export function useUpdateModel(
  projectId: string | number,
): UseMutationResult<Model, Error, UpdateModelVariables> {
  const queryClient = useQueryClient();
  return useMutation<Model, Error, UpdateModelVariables>({
    mutationFn: ({ modelId, data }) =>
      apiService.updateModel(Number(modelId), data),
    onSuccess: () => {
      invalidateModelKeys(queryClient, projectId);
    },
  });
}

/**
 * `useDeleteModel(projectId)` - mutation hook for
 * `DELETE /api/v1/models/:modelId`.
 *
 * @param projectId The project that owned the deleted model.
 *
 * @example
 *   const remove = useDeleteModel(projectId);
 *   if (confirm('Delete?')) await remove.mutateAsync({ modelId: 42 });
 */
export function useDeleteModel(
  projectId: string | number,
): UseMutationResult<void, Error, DeleteModelVariables> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteModelVariables>({
    mutationFn: ({ modelId }) =>
      apiService.deleteModel(Number(modelId)),
    onSuccess: () => {
      invalidateModelKeys(queryClient, projectId);
    },
  });
}
